import { apiRequest } from "./api.client";

export type KpiDirection = "up" | "down" | "flat";

export interface KpiCard {
  value: number;
  change_pct: number;
  direction: KpiDirection;
}

export interface TodaysAppointmentOut {
  appointment_id: string;
  start_time: string;
  patient_name: string;
  doctor_name: string;
  doctor_id: string;
  status: string;
  notes: string | null;
}

export interface PatientActivity {
  new_today: number;
  follow_ups_completed: number;
  active_cases: number;
}

export interface DepartmentOverviewRow {
  department: string;
  doctor_count: number;
  patient_count: number;
  appointment_count: number;
  revenue_paise: number | null;
}

export interface PrescriptionStats {
  issued_today: number;
  issued_this_week: number;
  issued_this_month: number;
  issued_total: number;
  monthly_average: number;
}

/** Backend shape: {type, severity, message}. Frontend may pass {level, title, detail}
 *  for hardcoded defaults — both are normalised at render time. */
export interface SystemAlert {
  type?: string;
  severity?: "error" | "warning" | "info" | "success";
  message?: string;
  // legacy frontend-default shape
  level?: "info" | "warning" | "success";
  title?: string;
  detail?: string;
}

export interface ActiveDoctorOut {
  doctor_id: string;
  name: string;
  specialization: string | null;
  status: string;
}

export interface RevenueTrendPoint {
  bucket: string;
  amount_paise: number;
}

export interface DashboardMetrics {
  total_patients: KpiCard;
  appointments_today: KpiCard;
  revenue_mtd_paise: KpiCard | null;
  active_doctors: KpiCard;
  active_doctors_total: number;
  active_doctors_list: ActiveDoctorOut[];
  todays_appointments: TodaysAppointmentOut[];
  patient_activity: PatientActivity;
  department_overview: DepartmentOverviewRow[];
  prescription_stats: PrescriptionStats;
  revenue_trend: RevenueTrendPoint[];
  system_alerts: SystemAlert[];
}

export interface PendingVisit {
  visit_id:        string;
  patient_id:      string;
  doctor_id:       string;
  patient_name:    string;
  visit_date:      string;
  chief_complaint: string | null;
  status:          string;
  current_step:    string;
  created_at:      string;
}

export interface DoctorKpis {
  my_appts_today: number;
  my_appts_done: number;
  pending_visit_count: number;
  prescriptions_this_week: number;
  total_patients: number;
}

export interface ReceptionKpis {
  appts_today: number;
  appts_checked_in: number;
  walkins_waiting: number;
  avg_wait_minutes: number | null;
  pending_payments_paise: number | null;
  pending_invoice_count: number;
  registered_today: number;
}

export interface MyPrescriptionRow {
  prescription_id:  string;
  visit_id:         string | null;
  patient_id:       string | null;
  prescribed_at:    string | null;
  visit_date:       string | null;
  status:           string;
  diagnosis_label:  string | null;
  patient_name:     string;
  medication_count: number;
}

// ── Role-scoped summary (Doctor / Receptionist dashboards) ───────────────────
// Powers the non-admin dashboards. Returns only the slice relevant to the
// caller's role; the other field is null. No revenue is exposed to non-admins.

export interface DoctorDashboardSummary {
  appointments_today: number;
  pending_soaps: number;
  prescriptions_today: number;
  total_patients: number;
  // Doctor KPI cards: draft (unfinalized) visits, count of finalized (active)
  // prescriptions the doctor wrote — matches "My Prescription History", shown as
  // "My Prescriptions" — and the clinic-wide active patient count.
  draft_visits: number;
  prescriptions_total: number;
  clinic_total_patients: number;
  todays_appointments: TodaysAppointmentOut[];
}

export interface ReceptionDashboardSummary {
  appointments_today: number;
  walk_ins_waiting: number;
  pending_payments_count: number;
  pending_payments_paise: number;
  registered_today: number;
  todays_appointments: TodaysAppointmentOut[];
}

export interface RoleDashboardSummary {
  role: string;
  doctor: DoctorDashboardSummary | null;
  reception: ReceptionDashboardSummary | null;
}

export const DashboardAPI = {
  getMetrics: () => apiRequest<DashboardMetrics>("/dashboard/metrics"),
  getPendingVisitCount: () =>
    apiRequest<{ pending_visit_count: number }>("/visits/pending-count"),
  getPendingVisits: () => apiRequest<PendingVisit[]>("/visits/pending"),
  getDoctorKpis: () => apiRequest<DoctorKpis>("/dashboard/doctor-kpis"),
  getReceptionKpis: () => apiRequest<ReceptionKpis>("/dashboard/reception-kpis"),
  getMyPrescriptions: (limit = 50) =>
    apiRequest<{ prescriptions: MyPrescriptionRow[] }>(`/visit/my-prescriptions?limit=${limit}`),
  getRoleSummary: () => apiRequest<RoleDashboardSummary>("/dashboard/summary"),
};
